import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck, LogOut } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * CambioPassword — pagina di cambio password obbligatorio al primo accesso
 * dopo un reset. L'utente non può navigare altrove finché non cambia la password.
 */
export default function CambioPassword() {
  const { user, logout, refreshMe } = useAuth();
  const nav = useNavigate();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd.length < 6) {
      setError("La nuova password deve essere di almeno 6 caratteri");
      return;
    }
    if (newPwd !== confirmPwd) {
      setError("Le due password non corrispondono");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success("Password aggiornata con successo");
      // Reload user to clear must_change_password
      await refreshMe?.();
      nav(user?.role === "agente" || user?.role === "platform_admin" ? "/admin" : "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md p-8 card-elevated border-slate-200" data-testid="cambio-password-page">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900">Cambia la tua password</h1>
            <div className="text-xs text-slate-500 mt-0.5">{user?.email}</div>
          </div>
        </div>

        {forced && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4 flex items-start gap-2" data-testid="must-change-banner">
            <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Per motivi di sicurezza devi impostare una nuova password. Quella attuale è
              temporanea e non potrà più essere usata.
            </span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="cambio-password-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              {forced ? "Password temporanea ricevuta via email" : "Password attuale"}
            </Label>
            <Input
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              required
              autoFocus
              data-testid="cp-current"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Nuova password (min 6 caratteri)</Label>
            <Input
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              required
              minLength={6}
              data-testid="cp-new"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Conferma nuova password</Label>
            <Input
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              required
              minLength={6}
              data-testid="cp-confirm"
            />
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="cp-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="cp-submit"
          >
            {submitting ? "Aggiornamento..." : "Aggiorna password"}
          </Button>
          {forced && (
            <button
              type="button"
              onClick={async () => { await logout(); nav("/login"); }}
              className="w-full text-xs text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1 mt-1"
              data-testid="cp-logout"
            >
              <LogOut className="w-3 h-3" /> Esci e riprova più tardi
            </button>
          )}
        </form>
      </Card>
    </div>
  );
}
